import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
import PageFade from './components/PageFade';
import RequireAccess from './components/RequireAccess';
const HomeStadium = lazy(() => import('./pages/HomeStadium'));
const PublicMatchPage = lazy(() => import('./pages/PublicMatchPage'));
const StaffApp = lazy(() => import('./pages/StaffApp'));
const CoordinatorView = lazy(() => import('./pages/CoordinatorView'));
const DriverView = lazy(() => import('./pages/DriverView'));
const OrganiserPanel = lazy(() => import('./pages/OrganiserPanel'));
const TournamentDetail = lazy(() => import('./pages/TournamentDetail'));
const RegisterTeam = lazy(() => import('./pages/RegisterTeam'));
const Login = lazy(() => import('./pages/Login'));
const RegisterPage = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const TournamentsMarketplace = lazy(() => import('./pages/TournamentsMarketplace'));
const LiveScoresHub = lazy(() => import('./pages/LiveScoresHub'));
const OrganisersPage = lazy(() => import('./pages/OrganisersPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TeamRegistrations = lazy(() => import('./pages/TeamRegistrations'));
const TeamZone = lazy(() => import('./pages/team/TeamZone'));
const Account = lazy(() => import('./pages/Account'));
const SuperHome = lazy(() => import('./pages/SuperHome'));
const FindWizard = lazy(() => import('./pages/FindWizard'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));

// Old match-page links (/t/{slug}) now live under /live/{slug}.
const MatchRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/live/${slug}`} replace />;
};

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin, logout } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
    return (
      <div className="tf-public flex min-h-screen items-center justify-center px-6 text-center">
        <div role="alert" className="max-w-sm rounded-tf-card border border-tf-line bg-tf-card p-6">
          <h1 className="font-bold">Account access unavailable</h1>
          <p className="mt-2 text-sm text-tf-muted">{authError.message || "Matchbase could not verify this account."}</p>
          <button type="button" onClick={() => logout(true)} className="mt-4 inline-flex h-11 items-center rounded-tf-control bg-tf-blue px-5 font-semibold text-white">Sign out</button>
        </div>
      </div>
    );
  }

  // Render the main app
  return (
    <PageFade>
    <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center" aria-label="Loading page"><div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" /></div>}>
    <Routes>
      {/* Add your page Route elements here */}
      <Route path="/" element={<HomeStadium />} />
      <Route path="/tournaments" element={<TournamentsMarketplace />} />
      <Route path="/tournaments/:slug" element={<TournamentDetail />} />
      <Route path="/tournament/:slug" element={<TournamentDetail />} />
      <Route path="/find" element={<FindWizard />} />
      <Route path="/live" element={<LiveScoresHub />} />
      <Route path="/live/:slug" element={<PublicMatchPage />} />
      <Route path="/t/:slug" element={<MatchRedirect />} />
      <Route path="/organisers" element={<OrganisersPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/team" element={<RequireAccess roles={['team_manager','team_assistant','guardian','organization_owner','organization_admin','operations']} area="your Team Zone"><TeamRegistrations /></RequireAccess>} />
      <Route path="/team/:registrationId" element={<RequireAccess roles={['team_manager','team_assistant','guardian','organization_owner','organization_admin','operations']} area="your Team Zone"><TeamZone /></RequireAccess>} />
      <Route path="/account" element={<Account />} />
      <Route path="/super" element={<RequireAccess roles={['platform_super_admin']} area="the platform admin area"><SuperHome /></RequireAccess>} />
      <Route path="/register/:slug" element={<RequireAccess area="team registration"><RegisterTeam /></RequireAccess>} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/confirm" element={<AuthCallback />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/staff" element={<RequireAccess roles={['scorekeeper','organization_owner','organization_admin','operations']} area="the scorekeeper app"><StaffApp /></RequireAccess>} />
      <Route path="/staff/transport" element={<RequireAccess roles={['organization_owner','organization_admin','operations']} area="the transport coordinator view"><CoordinatorView /></RequireAccess>} />
      <Route path="/driver/:token" element={<DriverView />} />
      <Route path="/admin" element={<RequireAccess roles={['platform_super_admin','organization_owner','organization_admin','finance','operations']} area="the organiser back office"><OrganiserPanel /></RequireAccess>} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
    </PageFade>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App
